import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { getErrorMessage } from "services/api";
import { useStores } from "shared-apis";
import { getStations, deleteStation } from "features/Stations/api";
import { StationModal } from "features/Stations/StationModal";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText } from "components/Text";
import { Button } from "components/Button";
import { Table } from "components/Table";
import { SearchInput } from "components/SearchInput";
import { useModal, ConfirmationModal, InfoModal } from "components/Modal";

import { numberComparator } from "components/Table/sortHelpers";

import styles from "./styles.module.scss";

export const StationsPage = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const stationsQuery = useQuery(["stations"], getStations);
  const stations = useMemo(
    () => stationsQuery.data?.items ?? [],
    [stationsQuery.data]
  );

  const storesQuery = useStores();
  const storeNameById = useMemo(() => {
    const map = new Map();
    for (const s of storesQuery.data?.items ?? []) {
      map.set(String(s.storesid), s.name);
    }
    return map;
  }, [storesQuery.data]);

  const rowData = useMemo(
    () =>
      stations.map((s) => ({
        stationid: s.stationid,
        name: s.name,
        storeName: storeNameById.get(String(s.storesid)) || "",
        priority: Number(s.priority),
        busy: s.deviceuniqueid ? "Да" : "Нет",
        deviceuniqueid: s.deviceuniqueid || "",
      })),
    [stations, storeNameById]
  );

  const filtered = useMemo(() => {
    if (!search) return rowData;
    const lower = search.toLowerCase();
    return rowData.filter((s) =>
      [s.name, s.storeName, s.stationid, s.deviceuniqueid]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(lower))
    );
  }, [rowData, search]);

  const colDefs = useMemo(
    () => [
      { field: "stationid", headerName: "Код", width: 90 },
      { field: "name", headerName: "Наименование", flex: 2, minWidth: 200 },
      { field: "storeName", headerName: "Склад продаж", flex: 1, minWidth: 160 },
      {
        field: "priority",
        headerName: "Приоритет",
        width: 115,
        comparator: numberComparator,
      },
      { field: "busy", headerName: "Занята", width: 95 },
      {
        field: "deviceuniqueid",
        headerName: "Устройство",
        flex: 2,
        minWidth: 200,
      },
    ],
    []
  );

  const openStationModal = (station) =>
    openModal(<StationModal station={station} stations={stations} />);

  const onDeleteClick = () => {
    if (!selected) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить станцию "${selected.name}"?`}
        onConfirm={async () => {
          try {
            await deleteStation(selected.stationid);
            setSelected(null);
            queryClient.invalidateQueries(["stations"]);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <div className={styles.page}>
      <PageLayout
        header={<BoldText>Станции</BoldText>}
        footer={
          <ActionsRow>
            <Button icon="plus" onClick={() => openStationModal(null)}>
              Добавить станцию
            </Button>
            <Button
              icon="delete"
              onClick={onDeleteClick}
              disabled={!selected}
              title="Удалить станцию"
            />
            <Button
              icon="info"
              onClick={() => openStationModal(selected)}
              disabled={!selected}
              title="Свойства станции"
            />
          </ActionsRow>
        }
        bodyFill
      >
        <div className={styles.searchRow}>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Поиск"
          />
        </div>
        <Table
          fill
          rowData={filtered}
          colDefs={colDefs}
          rowKey="stationid"
          selectedId={selected?.stationid}
          onSelect={(row) =>
            setSelected(
              stations.find((s) => s.stationid === row.stationid) || null
            )
          }
          onOpen={(row) => {
            const station = stations.find(
              (s) => s.stationid === row.stationid
            );
            if (station) {
              setSelected(station);
              openStationModal(station);
            }
          }}
          loading={stationsQuery.isLoading}
          error={
            stationsQuery.isError ? getErrorMessage(stationsQuery.error) : ""
          }
          onRetry={stationsQuery.refetch}
        />
      </PageLayout>
    </div>
  );
};
